import getEquipmentObjFromAssignments from './getEquipmentObjFromAssignments';

export const EQUIPMENT_SLOTS = [
  'helmet',
  'torsoe',
  'boots',
  'weapon',
  'off_hand',
  'left_ring',
  'right_ring',
] as const;

type LoadoutUnitLike = { handle: string; hero_build_id?: string; engagement?: unknown };
type BuildLike = { _id?: string; hero_build_id?: string; equipment_assignment?: unknown };
type RosterHeroLike = { bags?: unknown };

/**
 * Sums the `loot_bonus` of every equipped item across a party (the Lucky / Unlucky prefixes).
 * Consumers apply it as a relative multiplier: `chance *= 1 + bonus`.
 *
 * Accepts both the battle-server team shape (`builds[handle]` is one resolved build) and the
 * dt-api / dt-cron shape (`builds[handle]` is an array of builds keyed by `hero_build_id`).
 */
export default function getPartyLootBonus({
  roster,
  builds,
  loadout,
  hero_handles,
}: {
  roster?: Record<string, RosterHeroLike | null | undefined> | null;
  builds?: Record<string, BuildLike | BuildLike[] | null | undefined> | null;
  loadout?: LoadoutUnitLike[] | Record<string, LoadoutUnitLike> | null;
  /** Optional whitelist of participating heroes, e.g. livingHeroHandles or team.positions. */
  hero_handles?: string[] | null;
}): number {
  if (!roster || !builds || !loadout) {
    return 0;
  }

  const loadout_units: LoadoutUnitLike[] = Array.isArray(loadout)
    ? loadout
    : Object.values(loadout);
  const whitelist = hero_handles ? new Set(hero_handles) : null;

  let bonus = 0;

  for (const loadout_unit of loadout_units) {
    const handle = loadout_unit?.handle;
    // benched heroes never fight, so their gear never counts (cf. getTeamForMatchType,
    // initNewCavernsMap, which both select the engaged party the same way)
    if (!handle || !loadout_unit.engagement || (whitelist && !whitelist.has(handle))) {
      continue;
    }
    const roster_hero = roster[handle];
    if (!roster_hero) {
      continue;
    }

    const raw_builds = builds[handle];
    const unit_build = Array.isArray(raw_builds)
      ? raw_builds.find(
          // both field names are in play during the Mongo -> Prisma migration
          (b) =>
            b?.hero_build_id === loadout_unit.hero_build_id || b?._id === loadout_unit.hero_build_id
        )
      : raw_builds;
    if (!unit_build) {
      continue;
    }

    // the helper is untyped JS, so name the only shape we care about here
    const equipment = getEquipmentObjFromAssignments({
      unit_bags: roster_hero.bags,
      unit_build,
    }) as Record<string, { loot_bonus?: number | null } | undefined>;
    for (const slot of EQUIPMENT_SLOTS) {
      bonus += equipment[slot]?.loot_bonus ?? 0;
    }
  }

  return bonus;
}
